import { StoreDayHours, StoreWeekHours } from '@core/models/store.state';

/**
 * Pure helpers over a store's structured weekly hours (`StoreWeekHours`,
 * Sunday-first). Everything takes `now` explicitly so labels stay honest and
 * testable - no hidden clock reads.
 */

/** Sunday-first day names, index-aligned with `Date.getDay()` (and valid schema.org `dayOfWeek` values). */
export const WEEK_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

/** "20:00" -> minutes from midnight (1200). */
const toMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/** "20:00" -> "8 PM"; "09:30" -> "9:30 AM". */
export function formatHoursTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const clockHour = hours % 12 === 0 ? 12 : hours % 12;
  return minutes === 0 ? `${clockHour} ${period}` : `${clockHour}:${String(minutes).padStart(2, '0')} ${period}`;
}

/** One day's display window, e.g. "9 AM - 8 PM" or "Closed". */
export function formatDayHours(day: StoreDayHours | null): string {
  return day === null ? 'Closed' : `${formatHoursTime(day.opens)} - ${formatHoursTime(day.closes)}`;
}

export interface StoreOpenState {
  isOpen: boolean;
  /** Honest live label, e.g. "Open until 8 PM" or "Closed - opens 10 AM tomorrow". */
  label: string;
}

/** The store's live open/closed state at `now`, with a shopper-facing label. */
export function storeOpenState(week: StoreWeekHours, now: Date): StoreOpenState {
  const day = now.getDay();
  const minutesNow = now.getHours() * 60 + now.getMinutes();
  const today = week[day] ?? null;

  if (today !== null && minutesNow >= toMinutes(today.opens) && minutesNow < toMinutes(today.closes)) {
    return { isOpen: true, label: `Open until ${formatHoursTime(today.closes)}` };
  }
  if (today !== null && minutesNow < toMinutes(today.opens)) {
    return { isOpen: false, label: `Closed - opens ${formatHoursTime(today.opens)} today` };
  }
  for (let ahead = 1; ahead <= 7; ahead++) {
    const next = week[(day + ahead) % 7] ?? null;
    if (next === null) continue;
    const when = ahead === 1 ? 'tomorrow' : WEEK_DAY_NAMES[(day + ahead) % 7];
    return { isOpen: false, label: `Closed - opens ${formatHoursTime(next.opens)} ${when}` };
  }
  return { isOpen: false, label: 'Temporarily closed' };
}

/**
 * schema.org `openingHoursSpecification` entries built from the real weekly
 * hours (CAT-08). Days sharing an identical window are grouped into one
 * specification; closed days are omitted, per the schema.org convention.
 */
export function openingHoursSpecification(week: StoreWeekHours): Record<string, unknown>[] {
  const groups = new Map<string, { days: string[]; opens: string; closes: string }>();
  week.forEach((day, index) => {
    if (day === null || index >= WEEK_DAY_NAMES.length) return;
    const key = `${day.opens}-${day.closes}`;
    const group = groups.get(key) ?? { days: [], opens: day.opens, closes: day.closes };
    group.days.push(WEEK_DAY_NAMES[index]);
    groups.set(key, group);
  });
  return [...groups.values()].map(group => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: group.days,
    opens: group.opens,
    closes: group.closes
  }));
}
